import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import AuthContext from "./AuthContext";
import { decksApi } from "../services/api";
import { fetchAllPages } from "../hooks/utils";

const DecksContext = createContext(null);

export const DecksProvider = ({ children }) => {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True once a fetch has succeeded — "no decks" is only known after that
  // (Review uses it to spot a ?deck= that no longer exists).
  const [loaded, setLoaded] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);
  // Refreshes overlap (Review refreshes after every rating; a cold load of
  // Review starts two): only the newest request may set state, or a slow
  // earlier response lands last and puts back stale counts or names.
  const generation = useRef(0);

  const fetchDecks = useCallback(async () => {
    const request = ++generation.current;
    const isLatest = () => request === generation.current;
    if (!isLoggedIn) {
      setDecks([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Every page: the list is paginated at 12, and a 13th deck used to
      // vanish from every picker and filter (study-decks).
      const { items } = await fetchAllPages((page) =>
        decksApi.getAll({ page }),
      );
      if (isLatest()) {
        setDecks(items);
        setLoaded(true);
      }
    } catch (err) {
      if (!isLatest()) return;
      console.error("Error fetching decks:", err);
      // Keep the last good list: a blip on a background refresh must not
      // make every deck vanish (Review's chips, the Decks page).
      setError("Failed to fetch decks");
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [isLoggedIn]);

  // Fetch decks when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      fetchDecks();
    } else {
      setDecks([]);
      setLoaded(false);
    }
  }, [isLoggedIn, fetchDecks]);

  const contextValue = {
    decks,
    loading,
    loaded,
    error,
    refreshDecks: fetchDecks,
  };

  return (
    <DecksContext.Provider value={contextValue}>
      {children}
    </DecksContext.Provider>
  );
};

DecksProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DecksContext;
